import classNames from 'classnames';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import { useSnapshot } from 'valtio';
import { RequestType } from '~/common/types';
import HoverCard from '~/components2/HoverCard';
import type { UtilityAgency } from '../../common';
import { activityMap } from '../../drawers/ActivityFeed/ActivityList';
import store from '../../store';
import CellDate from '../CellDate';
import { requestMatchesRow } from '../grouping';

interface Props {
  agency: UtilityAgency;
}

export default function LatestUpdateCell({ agency }: Props) {
  const { dataRequests } = useSnapshot(store) as typeof store;
  const updateRows: ReactNode[] = [];

  if (agency.availableDigitally) {
    updateRows.push(
      <HoverCard
        key="digital"
        side="left"
        trigger={
          <div className="cursor-pointer">
            <CellDate date={agency.lastRefresh} />
            [D] Data updated, refreshed {agency.refreshCadence?.toLowerCase()}.
          </div>
        }
        classNames="w-72 flex flex-col gap-2"
      >
        <div className="my-2">
          <div className="text-2xs uppercase text-secondary">
            {agency.lastRefresh?.toLocaleString(DateTime.DATE_MED)}
          </div>
          <div className="text-xs">
            Digital data updated by CivilGrid. This data is updated {agency.refreshCadence?.toLowerCase()}.
          </div>
        </div>
      </HoverCard>,
    );
  }

  // look for other updates
  const requests = Object.values(dataRequests).filter((d) => requestMatchesRow(d, agency));
  if (requests.length > 0) {
    requests.forEach((r) => {
      const activities = r.activities.map((a, i) => {
        const name = <span>{a.user?.name || a.user?.email} </span>;
        const Component = activityMap[a.activityType];
        return (
          <div key={a.id} className={classNames({ 'bg-secondary-light/50': i % 2 !== 0 })}>
            {Component && (
              <div className="my-2 mb-8 p-2">
                <div className="text-2xs uppercase text-secondary">{a.createdAt.toLocaleString(DateTime.DATE_MED)}</div>
                <div>
                  {name}
                  <Component activity={a} includeAgencyName={false} />
                </div>
              </div>
            )}
          </div>
        );
      });

      const mostRecentActivity = r.activities[r.activities.length - 1]; // undefined for demo-copied requests (activities not copied)
      let trigger: ReactNode;
      if (r.notes) {
        trigger = r.notes; // if note exists, always display it.
      } else if (mostRecentActivity) {
        const Component = activityMap[mostRecentActivity.activityType];
        trigger = <Component activity={mostRecentActivity} compact={true} />;
      } else {
        return; // no notes and no activities — nothing to show for this request
      }

      const mostRecentPill = (
        <div key={`pill-${r.id}`}>
          <CellDate date={mostRecentActivity?.createdAt} />
          <span className="mr-1">[{r.type === RequestType.MapsRequested ? 'M' : 'AB'}]</span>
          {trigger}
        </div>
      );

      updateRows.push(
        <HoverCard key={r.id} classNames="w-96 max-h-72 overflow-y-scroll" trigger={mostRecentPill} side="left">
          {activities.reverse()}
          <div className="p-2 font-mono text-xs">[Ref: {r.requestId}]</div>
        </HoverCard>,
      );
    });
  }

  return <div className="flex flex-col space-y-2">{updateRows}</div>;
}
